/**
 * SettingToggleItem
 * Flutter source: setting_toggle_item.dart
 *
 * Displays a single toggle setting with title, optional premium icon,
 * toggle switch, and description. Pure UI component — reactive value
 * updates are handled by the useUserSettings hook.
 */

import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { ToggleSwitch } from '@/presentation/components/common/controls/ToggleSwitch';
import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';
import type { UserSettingStorage } from '@/domain/entities/secureStorage/secureStorageEntities';

interface SettingToggleItemProps {
  setting: UserSettingStorage;
  isToggling: boolean;
  onToggle: () => void;
}

export const SettingToggleItem = ({
  setting,
  isToggling,
  onToggle,
}: SettingToggleItemProps) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  const currentValue = (setting.value ?? setting.defaultValue) === 1;

  return (
    <div
      style={{
        padding: `${responsive.spacing.base}px 0`,
        opacity: isToggling ? 0.7 : 1,
        transition: `opacity ${ANIMATION_CONSTANTS.duration.fast}ms ${ANIMATION_CONSTANTS.easing.ease}`,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            gap: responsive.gap.sm,
          }}
        >
          <span
            style={{
              fontSize: responsive.font.base,
              fontWeight: responsive.fontWeight.medium,
              color: theme.text.primary,
            }}
          >
            {setting.title}
          </span>
          {setting.premiumPackage && (
            <AssetIcon
              path="/assets/images/premium.png"
              width={responsive.icon.xs}
              height={responsive.icon.xs}
              color={theme.exclusive}
            />
          )}
        </div>
        <ToggleSwitch
          value={currentValue}
          onChange={() => onToggle()}
          disabled={isToggling}
          qa={{ id: `settings-toggle-${setting.name}`, value: currentValue ? 'on' : 'off' }}
        />
      </div>
      <div
        style={{
          fontSize: responsive.font.sm,
          color: theme.text.secondary,
          marginTop: responsive.spacing.xxxs,
          paddingRight: responsive.spacing.xl,
        }}
      >
        {setting.description}
      </div>
    </div>
  );
};
